import { TProfessional } from 'types/appointment';

import {
  BookableAppointment,
  BookableAppointmentState,
} from './bookableAppointments';

export type FlattenedAppointment = {
  providerGuid?: string;
  serviceGuid: string;
  bookingOrder?: number;
};

/**
 * Flattens the appointments array into an array of FlattenedAppointments
 * @param appointments the appointments to sort
 * @returns BookableAppointment[]
 */
export function flattenAppointments(
  appointments: BookableAppointment[],
): FlattenedAppointment[] {
  return appointments.reduce((acc, { providerGuid, services }) => {
    return [
      ...acc,
      ...services.map(({ guid, bookingOrder }) => ({
        providerGuid,
        serviceGuid: guid,
        bookingOrder,
      })),
    ];
  }, [] as FlattenedAppointment[]);
}

/**
 * Unflattens the appointments array into an array of BookableAppointments
 *
 * @param flattenedAppointments
 * @returns
 */
export const unflattenAppointments = (
  flattenedAppointments: FlattenedAppointment[],
): BookableAppointment[] => {
  const appointments: BookableAppointment[] = [];

  let currentAppointment: BookableAppointment | undefined;

  flattenedAppointments.forEach((flattenedAppointment) => {
    const { providerGuid, serviceGuid, bookingOrder } = flattenedAppointment;

    currentAppointment = {
      providerGuid,
      services: [{ guid: serviceGuid, bookingOrder }],
    };
    appointments.push(currentAppointment);
  });
  return appointments;
};

type sequence = {
  length: number;
  sequence: string[];
};

function longestSequence(
  arr: string[][],
  index = 0,
  lastNumber?: string,
): sequence {
  if (index === arr.length) {
    return { length: 0, sequence: [] };
  }
  let maxSequence: sequence = { length: -1, sequence: [] };

  for (const num of arr[index]) {
    const next = longestSequence(arr, index + 1, num);
    const sequenceLength = next.length + (num === lastNumber ? 1 : 0);

    if (sequenceLength > maxSequence.length) {
      maxSequence = {
        length: sequenceLength,
        sequence: [num, ...next.sequence],
      };
    }
  }

  return maxSequence;
}

function findProviderForService(
  serviceOrOptionGUID: string,
  serviceProviders: TProfessional[],
) {
  const providers = [];

  for (const provider of serviceProviders) {
    for (const providerService of provider.services) {
      if (providerService.guid === serviceOrOptionGUID) {
        providers.push(provider.guid);
      } else {
        const providerOption = providerService.options?.find(
          (option) => option.guid === serviceOrOptionGUID,
        );

        providerOption && providers.push(provider.guid);
      }
    }
  }
  return providers;
}

function groupAppointments(
  appointments: BookableAppointment[],
  providers: string[],
): BookableAppointment[] {
  appointments.forEach((appointment, index) => {
    if (index < providers.length) {
      appointment.auxProviderGuid = providers[index];
    }
  });

  for (let i = appointments.length - 1; i > 0; i--) {
    if (
      appointments[i].auxProviderGuid === appointments[i - 1].auxProviderGuid
    ) {
      appointments[i - 1].services.push(...appointments[i].services);
      appointments.splice(i, 1);
    }
  }

  return appointments;
}

export function optimizeAppointments(
  appointments: BookableAppointment[],
  state: BookableAppointmentState,
): BookableAppointment[] {
  const providers: string[][] = [];

  for (const appointment of appointments) {
    const appGUID = appointment.providerGuid;
    const serviceGUID = appointment.services[0].guid;

    const serviceProviders =
      appGUID === undefined
        ? findProviderForService(serviceGUID, state.serviceProviders)
        : [appGUID];

    providers.push(serviceProviders);
  }

  const optimizedProviders = longestSequence(providers).sequence;

  const groupedAppointments = groupAppointments(
    appointments,
    optimizedProviders,
  );

  return groupedAppointments.map(({ providerGuid, services }) => ({
    providerGuid,
    services: services.map(({ guid, bookingOrder }) => ({
      guid,
      bookingOrder: bookingOrder === -1 ? undefined : bookingOrder,
    })),
  }));
}
